import * as React from 'react'
import { useQueryDrivers } from '@/lib/api/company/drivers/useQueryDrivers'
import { useQueryVehiclesList } from '@/lib/api/company/vehicle-list/useQueryVehiclesList'
import { ChartEditor } from './useChartEditor'
import { SearchSelect } from './SearchSelect'
import { parseChartIso } from '@/modules/imports/components/TachographChartCanvas'
import { ChartT, useChartT } from '../i18n'
import { TACHOGRAPH_STATUSES } from '../tachographStatus'

// WPF SuperTacho/Views/Import/TachographChartImportView.xaml:261-357 — the entire
// "Dane tarczki" group: day boundaries, driver, vehicle, odometer fields, status,
// crew + closed checkboxes.

type Props = {
  companyId: string
  editor: ChartEditor
}

export function ChartFormSidebar({ companyId, editor }: Props) {
  const T = useChartT()
  const distance = Math.max(0, editor.firstVehicleOdometerEnd - editor.firstVehicleOdometerStart)
  return (
    <div className="tw-rounded tw-border tw-border-slate-200 tw-bg-white">
      <div className="tw-border-b tw-border-slate-200 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
        {T('discData', 'Dane tarczki')}
      </div>
      <div className="tw-flex tw-flex-col tw-gap-2 tw-px-3 tw-py-3">
        <Field label={T('colDay', 'Dzień')}>
          <input
            type="date"
            value={dateOnly(editor.dayStartDate)}
            onChange={(e) => editor.setChartDate(e.target.value)}
            className="tw-w-full tw-rounded tw-border tw-border-slate-300 tw-px-2 tw-py-1 tw-text-sm"
          />
        </Field>

        <div className="tw-grid tw-grid-cols-2 tw-gap-2">
          <Field label={T('dayStart', 'Początek dnia')}>
            <HHMMInput
              value={hhmm(editor.dayStartDate)}
              onChange={editor.setDayStartTime}
            />
          </Field>
          <Field label={T('dayEnd', 'Koniec dnia')}>
            <HHMMInput
              value={hhmm(editor.dayEndDate)}
              onChange={editor.setDayEndTime}
            />
          </Field>
        </div>

        <DriverPicker companyId={companyId} editor={editor} T={T} />
        <VehiclePicker companyId={companyId} editor={editor} T={T} />

        <div className="tw-grid tw-grid-cols-2 tw-gap-2">
          <Field label={T('odometerStart', 'Start (km)')}>
            <input
              type="number"
              value={editor.firstVehicleOdometerStart || ''}
              onChange={(e) => editor.setOdometerStart(parseInt(e.target.value || '0', 10))}
              className="tw-w-full tw-rounded tw-border tw-border-slate-300 tw-px-2 tw-py-1 tw-text-sm"
            />
          </Field>
          <Field label={T('odometerEnd', 'Koniec (km)')}>
            <input
              type="number"
              value={editor.firstVehicleOdometerEnd || ''}
              onChange={(e) => editor.setOdometerEnd(parseInt(e.target.value || '0', 10))}
              className="tw-w-full tw-rounded tw-border tw-border-slate-300 tw-px-2 tw-py-1 tw-text-sm"
            />
          </Field>
        </div>
        <div className="tw-grid tw-grid-cols-2 tw-gap-2">
          <Field label={T('distance', 'Przebieg (km)')}>
            <input
              type="number"
              value={distance}
              readOnly
              className="tw-w-full tw-rounded tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-2 tw-py-1 tw-text-sm"
            />
          </Field>
          <Field label={T('distanceFromChart', 'Z tarczki (km)')}>
            <input
              type="number"
              value={editor.distanceFromChart || ''}
              onChange={(e) => editor.setDistanceFromChart(parseInt(e.target.value || '0', 10))}
              className="tw-w-full tw-rounded tw-border tw-border-slate-300 tw-px-2 tw-py-1 tw-text-sm"
            />
          </Field>
        </div>

        <Field label={T('status', 'Status')}>
          <select
            value={editor.tachographStatus}
            onChange={(e) => editor.setTachographStatus(parseInt(e.target.value, 10))}
            className="tw-w-full tw-rounded tw-border tw-border-slate-300 tw-px-2 tw-py-1 tw-text-sm"
          >
            {TACHOGRAPH_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {T(s.key, s.def)}
              </option>
            ))}
          </select>
        </Field>

        <div className="tw-flex tw-gap-4 tw-pt-1">
          <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
            <input
              type="checkbox"
              checked={editor.isCrewWork}
              onChange={(e) => editor.setIsCrewWork(e.target.checked)}
            />
            {T('crewWork', 'Załoga')}
          </label>
          <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
            <input
              type="checkbox"
              checked={editor.isClosed}
              onChange={(e) => editor.setIsClosed(e.target.checked)}
            />
            {T('closed', 'Zamknięta')}
          </label>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="tw-block tw-text-xs tw-font-medium tw-text-slate-700">{label}</label>
      {children}
    </div>
  )
}

// 24-hour HH:mm input. Avoids <input type="time"> which renders as 12-hour AM/PM
// in en-US browsers; WPF always shows HH:mm.
function HHMMInput({
  value,
  onChange,
}: {
  value: string
  onChange: (hhmm: string) => void
}) {
  const [local, setLocal] = React.useState(value)
  React.useEffect(() => setLocal(value), [value])
  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]{1,2}:[0-9]{2}"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(local)) onChange(local)
        else setLocal(value)
      }}
      className="tw-w-full tw-rounded tw-border tw-border-slate-300 tw-px-2 tw-py-1 tw-text-sm tw-font-mono"
    />
  )
}

function DriverPicker({
  companyId,
  editor,
  T,
}: {
  companyId: string
  editor: ChartEditor
  T: ChartT
}) {
  const useSearch = (search: string) => {
    const q = useQueryDrivers({ companyId, search, pageSize: 25, isActive: true })
    const list = (q.data as any) || []
    const drivers = Array.isArray(list) ? list : list.drivers || []
    return {
      items: drivers.map((d: any) => ({ id: d.id, label: d.fullName || `${d.surname} ${d.name}` })),
      isLoading: q.isLoading,
    }
  }
  return (
    <SearchSelect
      label={T('colDriver', 'Kierowca')}
      placeholder={T('searchDriver', 'Wyszukaj kierowcę...')}
      value={editor.driverId}
      onChange={editor.setDriverId}
      useSearch={useSearch}
      rightSlot={
        <a
          href={`/et/company/${companyId}/driverModule`}
          target="_blank"
          rel="noreferrer"
          title={T('addDriverHint', 'Dodaj nowego kierowcę (otwiera moduł kierowców)')}
          className="tw-flex tw-h-8 tw-items-center tw-justify-center tw-rounded tw-border tw-border-slate-300 tw-px-2 tw-text-base tw-text-slate-600 hover:tw-bg-slate-50"
        >
          +
        </a>
      }
    />
  )
}

function VehiclePicker({
  companyId,
  editor,
  T,
}: {
  companyId: string
  editor: ChartEditor
  T: ChartT
}) {
  const useSearch = (search: string) => {
    const q = useQueryVehiclesList({ companyId, search, pageSize: 25, isActive: true })
    const list = (q.data as any) || []
    const vehicles = Array.isArray(list) ? list : list.vehicles || []
    return {
      items: vehicles.map((v: any) => ({
        id: v.id,
        label: v.registrationNumber || v.registrationNumberFromTachograph || `#${v.id}`,
      })),
      isLoading: q.isLoading,
    }
  }
  return (
    <SearchSelect
      label={T('colVehicle', 'Pojazd')}
      placeholder={T('searchVehicle', 'Wyszukaj pojazd...')}
      value={editor.vehicleId}
      onChange={editor.setVehicleId}
      useSearch={useSearch}
      rightSlot={
        <a
          href={`/et/company/${companyId}/vehicleModule/list`}
          target="_blank"
          rel="noreferrer"
          title={T('addVehicleHint', 'Dodaj nowy pojazd (otwiera moduł pojazdów)')}
          className="tw-flex tw-h-8 tw-items-center tw-justify-center tw-rounded tw-border tw-border-slate-300 tw-px-2 tw-text-base tw-text-slate-600 hover:tw-bg-slate-50"
        >
          +
        </a>
      }
    />
  )
}

function dateOnly(iso: string): string {
  return iso.slice(0, 10)
}
function hhmm(iso: string): string {
  const d = parseChartIso(iso)
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
}
// Emit a naive (zone-less) timestamp so it round-trips through parseChartIso /
// toChartIso unchanged — see discPolar.parseChartIso.
